/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OfferLetterTemplatesService {

    /**
     * Requests and Pricing tabs of the Document Signing page
     * @returns any returns signature-request usage and the rate card
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorDocumentSigningSettings(): CancelablePromise<{
        /**
         * monolith path the page's "Company settings" back link points at — /:slug/account/edit for account managers, /:slug/brands for brand-based regular users, null for a user the monolith shows no Company settings entry to
         */
        company_settings_path?: string | null;
        /**
         * false for brand-based regular users, who never saw signature-request usage on the legacy page, and for users without the `embedded_signature_templates` permission, which that page required. When false, `requests` is empty regardless of the account data
         */
        requests_visible?: boolean;
        /**
         * requires both `requests_visible` and the account's Display Pricing feature. When false, `pricing_tiers` is empty and `pricing_effective_on` is null
         */
        pricing_visible?: boolean;
        /**
         * one row per billed month, oldest first
         */
        requests?: Array<{
            month?: string;
            request_count?: number | null;
            /**
             * USD
             */
            charge?: number | null;
        }>;
        /**
         * the document signing rate card — fixed, identical for every account
         */
        pricing_tiers?: Array<{
            name?: string;
            maximum_signature_request_count?: number;
            /**
             * USD per month
             */
            price?: number;
        }>;
        /**
         * the date the rate card takes effect — 2018-09-01 for accounts on the newer document signing billing, 2017-08-01 otherwise
         */
        pricing_effective_on?: string | null;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/document_signing_settings',
            errors: {
                403: `offer letter templates are not enabled for the account`,
            },
        });
    }

    /**
     * List the Document Signing Templates tab, merged (offer-letter + legacy)
     * @param query title search (ILIKE) applied to both arms
     * @param page
     * @param sort column to sort by; an unrecognised or absent value falls back to title
     * @param direction sort direction on the `sort` column; anything other than "desc" is ascending
     * @returns any a manage_funnels-only user sees offer-letter rows with the legacy arm empty
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorDocumentTemplates(
        query?: string,
        page?: number,
        sort?: 'title' | 'updated_at',
        direction?: 'asc' | 'desc',
    ): CancelablePromise<{
        document_templates?: Array<{
            /**
             * discriminates the row shape: offer_letter (docx, has variables/conditional blocks) or legacy (static document, EmbeddedSignatureTemplate)
             */
            kind?: 'offer_letter' | 'legacy';
            /**
             * offer_letter: external_id (UUID string). legacy: the integer primary key.
             */
            id?: (string | number);
            title?: string;
            updated_at?: string;
            /**
             * true when the row belongs to an ancestor account and is read-only here
             */
            inherited?: boolean;
            available_to_sub_accounts?: boolean;
            description?: string | null;
            /**
             * offer_letter only
             */
            active?: boolean;
            /**
             * offer_letter only
             */
            variables_count?: number;
            /**
             * offer_letter only
             */
            mapped_variables_count?: number;
            /**
             * offer_letter only
             */
            conditions_count?: number;
            /**
             * offer_letter only
             */
            configured_conditions_count?: number;
            /**
             * offer_letter only
             */
            updated_by?: string | null;
            /**
             * both kinds: number of stages this row is assigned to. Null on an inherited row
             */
            usage_count?: number | null;
            /**
             * number of DISTINCT workflows holding this template — the figure the in-use notice counts in its sentence. Not usage_count, which counts stage assignments: one workflow can hold two document-signing stages that use the same template. Null on an inherited template, like usage_count (AXHE-4978)
             */
            workflow_usage_count?: number | null;
            /**
             * one entry per stage assignment, ordered by workflow then stage — the rows the in-use notice lists. Null on an inherited template (AXHE-4978)
             */
            usages?: Array<{
                stage_title?: string;
                /**
                 * the workflow holding the stage: a Workflow record on an account running the workflow system, otherwise the opening's own stage set
                 */
                workflow_title?: string;
                /**
                 * Recruiter UI path that opens the stage in the workflow editor, through its own opening or, for a stage a Workflow record owns, the first opening on this account that runs that workflow. Null when there is none, or when the viewer cannot edit it
                 */
                stage_path?: string | null;
            }> | null;
            /**
             * how many of the attached stages the in-use notice cannot list: one on another account in the tree, one on a private opening the viewer cannot see, one whose MultiStage parent is gone, one soft-deleted. The delete guard refuses over them, so a notice reading `usages` as the whole story would tell an admin to detach everything it names and still be refused. `usages.length` + this = the distinct stages attached, which can be less than usage_count because one stage may hold the template in two document sets. Null on an inherited row (AXHE-4978)
             */
            unlisted_usage_count?: number | null;
            /**
             * offer_letter only
             */
            signers?: Array<{
                /**
                 * unique signer key (roster identity)
                 */
                'k'?: string;
                /**
                 * display label (signer's name)
                 */
                'l'?: string;
                /**
                 * single-letter avatar initial
                 */
                's'?: string;
                /**
                 * avatar foreground color (hex)
                 */
                fg?: string;
                /**
                 * avatar background color (hex)
                 */
                bg?: string;
                /**
                 * true on the one entry that is the candidate (applicant); omitted or false on employer countersigners.
                 */
                'c'?: boolean;
            }>;
            /**
             * legacy only: not shown on the card; carried so the metadata edit drawer can open from a row already on the page
             */
            message?: string | null;
            /**
             * legacy only: signer role names, e.g. ["Applicant", "Reviewer"]. Read-only.
             */
            signer_roles?: Array<string>;
            /**
             * legacy only: monolith path that edits this row (the legacy #edit_fields screen). null on an inherited row.
             */
            edit_path?: string | null;
            /**
             * legacy only: true when a descendant account already has a stage assigned against this row via available_to_sub_accounts inheritance. The edit drawer disables the toggle for this reason, matching the legacy endpoint's #update guard.
             */
            in_use_by_sub_accounts?: boolean;
        }>;
        pagination?: {
            current_page?: number;
            next_page?: number | null;
            per_page?: number | null;
            total_count?: number;
            total_pages?: number;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/document_templates',
            query: {
                'query': query,
                'page': page,
                'sort': sort,
                'direction': direction,
            },
            errors: {
                400: `query or page is malformed — a null byte, not a bare string, or not a bare number`,
                403: `the offer-letter-templates flag is off`,
            },
        });
    }

    /**
     * List legacy (static) document templates for the Document Signing page
     * @param query search over title and message
     * @param page
     * @returns any returns the account legacy templates
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorEmbeddedSignatureTemplates(
        query?: string,
        page?: number,
    ): CancelablePromise<{
        embedded_signature_templates?: Array<{
            /**
             * EmbeddedSignatureTemplate primary key
             */
            id?: number;
            title?: string;
            message?: string | null;
            /**
             * signer role names, e.g. ["Applicant", "Reviewer"]. Read-only: the edit drawer does not offer changing these — see #update.
             */
            signer_roles?: Array<string>;
            available_to_sub_accounts?: boolean;
            updated_at?: string;
            /**
             * true when the template belongs to an ancestor account and is shared down via available_to_sub_accounts; read-only here
             */
            inherited?: boolean;
            /**
             * monolith path that edits this template — the legacy #edit_fields screen. NOT /:slug/embedded_signature_templates, which is #index and redirects straight back to this page. null on an inherited row, which this account cannot edit.
             */
            edit_path?: string | null;
            /**
             * true when a descendant account already has a stage assigned against this template via available_to_sub_accounts inheritance. #update blocks turning available_to_sub_accounts off while this is true (422); the edit drawer disables the toggle for the same reason.
             */
            in_use_by_sub_accounts?: boolean;
            /**
             * how many stages reference this template — the same total the destroy action refuses a delete over. null on an inherited row, whose stage assignments belong to the owning account.
             */
            usage_count?: number | null;
            /**
             * number of DISTINCT workflows holding this template — the figure the in-use notice counts in its sentence. Not usage_count, which counts stage assignments: one workflow can hold two document-signing stages that use the same template. Null on an inherited row, like usage_count (AXHE-4978)
             */
            workflow_usage_count?: number | null;
            /**
             * one entry per stage assignment, ordered by workflow then stage — the rows the in-use notice lists. Null on an inherited row (AXHE-4978)
             */
            usages?: Array<{
                stage_title?: string;
                /**
                 * the workflow holding the stage: a Workflow record on an account running the workflow system, otherwise the opening's own stage set
                 */
                workflow_title?: string;
                /**
                 * Recruiter UI path that opens the stage in the workflow editor, through its own opening or, for a stage a Workflow record owns, the first opening on this account that runs that workflow. Null when there is none, or when the viewer cannot edit it
                 */
                stage_path?: string | null;
            }> | null;
            /**
             * how many of the attached stages the in-use notice cannot list: one on another account in the tree, one on a private opening the viewer cannot see, one whose MultiStage parent is gone, one soft-deleted. The delete guard refuses over them, so a notice reading `usages` as the whole story would tell an admin to detach everything it names and still be refused. `usages.length` + this = the distinct stages attached, which can be less than usage_count because one stage may hold the template in two document sets. Null on an inherited row (AXHE-4978)
             */
            unlisted_usage_count?: number | null;
        }>;
        pagination?: {
            current_page?: number;
            next_page?: number | null;
            per_page?: number | null;
            total_count?: number;
            total_pages?: number;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/embedded_signature_templates',
            query: {
                'query': query,
                'page': page,
            },
            errors: {
                403: `the user cannot manage legacy document templates`,
            },
        });
    }

    /**
     * Save a PDF template the recruiter finished in the provider editor
     * @param requestBody
     * @returns any the template is saved
     * @throws ApiError
     */
    public static postInternalApiWorkflowEditorEmbeddedSignatureTemplates(
        requestBody: {
            embedded_signature_template: {
                /**
                 * the signed token returned by POST /draft. The provider template id and the signing roster are read out of THIS, never from the request body — vendor template ids are one namespace across every tenant, so the token is what says which account drafted it.
                 */
                draft_token: string;
                title: string;
                message?: string | null;
                /**
                 * role names in signing order. Must match the roster the draft was created with, which the provider has already baked into the placed fields; a roster that differs in name, case or order is refused with a 422.
                 */
                signer_roles: Array<string>;
                brand_id?: number | null;
                available_to_sub_accounts?: boolean;
            };
        },
    ): CancelablePromise<{
        /**
         * EmbeddedSignatureTemplate primary key
         */
        id?: number;
        title?: string;
        message?: string | null;
        /**
         * signer role names, e.g. ["Applicant", "Reviewer"]. Read-only: the edit drawer does not offer changing these — see #update.
         */
        signer_roles?: Array<string>;
        available_to_sub_accounts?: boolean;
        updated_at?: string;
        /**
         * true when the template belongs to an ancestor account and is shared down via available_to_sub_accounts; read-only here
         */
        inherited?: boolean;
        /**
         * monolith path that edits this template — the legacy #edit_fields screen. NOT /:slug/embedded_signature_templates, which is #index and redirects straight back to this page. null on an inherited row, which this account cannot edit.
         */
        edit_path?: string | null;
        /**
         * true when a descendant account already has a stage assigned against this template via available_to_sub_accounts inheritance. #update blocks turning available_to_sub_accounts off while this is true (422); the edit drawer disables the toggle for the same reason.
         */
        in_use_by_sub_accounts?: boolean;
        /**
         * how many stages reference this template — the same total the destroy action refuses a delete over. null on an inherited row, whose stage assignments belong to the owning account.
         */
        usage_count?: number | null;
        /**
         * number of DISTINCT workflows holding this template — the figure the in-use notice counts in its sentence. Not usage_count, which counts stage assignments: one workflow can hold two document-signing stages that use the same template. Null on an inherited row, like usage_count (AXHE-4978)
         */
        workflow_usage_count?: number | null;
        /**
         * one entry per stage assignment, ordered by workflow then stage — the rows the in-use notice lists. Null on an inherited row (AXHE-4978)
         */
        usages?: Array<{
            stage_title?: string;
            /**
             * the workflow holding the stage: a Workflow record on an account running the workflow system, otherwise the opening's own stage set
             */
            workflow_title?: string;
            /**
             * Recruiter UI path that opens the stage in the workflow editor, through its own opening or, for a stage a Workflow record owns, the first opening on this account that runs that workflow. Null when there is none, or when the viewer cannot edit it
             */
            stage_path?: string | null;
        }> | null;
        /**
         * how many of the attached stages the in-use notice cannot list: one on another account in the tree, one on a private opening the viewer cannot see, one whose MultiStage parent is gone, one soft-deleted. The delete guard refuses over them, so a notice reading `usages` as the whole story would tell an admin to detach everything it names and still be refused. `usages.length` + this = the distinct stages attached, which can be less than usage_count because one stage may hold the template in two document sets. Null on an inherited row (AXHE-4978)
         */
        unlisted_usage_count?: number | null;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/workflow_editor/embedded_signature_templates',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `the draft token is missing, expired, tampered with, another account's, or names a different roster than the one posted`,
            },
        });
    }

    /**
     * Hand a PDF to the signature provider and get its field-placement editor URL
     * @param formData
     * @returns any the provider accepted the document
     * @throws ApiError
     */
    public static postInternalApiWorkflowEditorEmbeddedSignatureTemplatesDraft(
        formData: {
            /**
             * the PDF. Validated on its leading bytes, not its name or content type.
             */
            'embedded_signature_template[file]': Blob;
            /**
             * the recruiter's name for the template
             */
            'embedded_signature_template[title]': string;
            /**
             * what the applicant sees the document called. The provider builds the signature-request subject from it.
             */
            'embedded_signature_template[document_title]'?: string;
            'embedded_signature_template[message]'?: string;
            /**
             * role names in signing order. Fixed at this point: the provider numbers roles here and bakes those numbers into every placed field.
             */
            'embedded_signature_template[signer_roles][]': Array<string>;
            /**
             * merge-field names from GET /merge_keys, which the recruiter may then place in the provider editor. Fixed at this point too, and harder: the provider refuses to add a merge field from inside that editor, so a key omitted here can never be placed on this document.
             */
            'embedded_signature_template[merge_keys][]'?: Array<string>;
        },
    ): CancelablePromise<{
        /**
         * the provider's id for the drafted template
         */
        template_id?: string;
        /**
         * short-lived URL of the field-placement editor
         */
        edit_url?: string;
        expires_at?: number | null;
        /**
         * signed claim on (account, template_id). Post it back to create the template; it is the only thing that proves this account drafted it.
         */
        draft_token?: string;
        /**
         * provider client id the browser SDK needs
         */
        client_id?: string;
        test_mode?: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/workflow_editor/embedded_signature_templates/draft',
            formData: formData,
            mediaType: 'multipart/form-data',
        });
    }

    /**
     * The merge keys this account may put on a signature template
     * @returns any returns the catalog, in the four groups the picker shows
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorEmbeddedSignatureTemplatesMergeKeys(): CancelablePromise<{
        merge_keys?: {
            standard?: Array<{
                /**
                 * what a recruiter reads, prefixed with its group
                 */
                label?: string;
                key?: string;
                identifier?: string;
            }>;
            custom?: Array<{
                /**
                 * what a recruiter reads, prefixed with its group
                 */
                label?: string;
                key?: string;
                identifier?: string;
            }>;
            /**
             * empty unless the account has ups-functionality or manage-customer-attributes
             */
            customer_attributes?: Array<{
                /**
                 * what a recruiter reads, prefixed with its group
                 */
                label?: string;
                key?: string;
                identifier?: string;
            }>;
            /**
             * empty unless the account has ups-functionality
             */
            partner?: Array<{
                /**
                 * what a recruiter reads, prefixed with its group
                 */
                label?: string;
                key?: string;
                identifier?: string;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/embedded_signature_templates/merge_keys',
        });
    }

    /**
     * A fresh field-placement editor URL for a template that already exists
     * @param id
     * @returns any returns a short-lived editor URL
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorEmbeddedSignatureTemplatesEditUrl(
        id: number,
    ): CancelablePromise<{
        edit_url?: string;
        expires_at?: number | null;
        client_id?: string;
        test_mode?: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/embedded_signature_templates/{id}/edit_url',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Update a legacy template's metadata (title, message, available_to_sub_accounts)
     * @param id
     * @param requestBody
     * @returns any updated
     * @throws ApiError
     */
    public static patchInternalApiWorkflowEditorEmbeddedSignatureTemplates(
        id: number,
        requestBody: {
            embedded_signature_template?: {
                title?: string;
                message?: string;
                available_to_sub_accounts?: boolean;
            };
        },
    ): CancelablePromise<{
        /**
         * EmbeddedSignatureTemplate primary key
         */
        id?: number;
        title?: string;
        message?: string | null;
        /**
         * signer role names, e.g. ["Applicant", "Reviewer"]. Read-only: the edit drawer does not offer changing these — see #update.
         */
        signer_roles?: Array<string>;
        available_to_sub_accounts?: boolean;
        updated_at?: string;
        /**
         * true when the template belongs to an ancestor account and is shared down via available_to_sub_accounts; read-only here
         */
        inherited?: boolean;
        /**
         * monolith path that edits this template — the legacy #edit_fields screen. NOT /:slug/embedded_signature_templates, which is #index and redirects straight back to this page. null on an inherited row, which this account cannot edit.
         */
        edit_path?: string | null;
        /**
         * true when a descendant account already has a stage assigned against this template via available_to_sub_accounts inheritance. #update blocks turning available_to_sub_accounts off while this is true (422); the edit drawer disables the toggle for the same reason.
         */
        in_use_by_sub_accounts?: boolean;
        /**
         * how many stages reference this template — the same total the destroy action refuses a delete over. null on an inherited row, whose stage assignments belong to the owning account.
         */
        usage_count?: number | null;
        /**
         * number of DISTINCT workflows holding this template — the figure the in-use notice counts in its sentence. Not usage_count, which counts stage assignments: one workflow can hold two document-signing stages that use the same template. Null on an inherited row, like usage_count (AXHE-4978)
         */
        workflow_usage_count?: number | null;
        /**
         * one entry per stage assignment, ordered by workflow then stage — the rows the in-use notice lists. Null on an inherited row (AXHE-4978)
         */
        usages?: Array<{
            stage_title?: string;
            /**
             * the workflow holding the stage: a Workflow record on an account running the workflow system, otherwise the opening's own stage set
             */
            workflow_title?: string;
            /**
             * Recruiter UI path that opens the stage in the workflow editor, through its own opening or, for a stage a Workflow record owns, the first opening on this account that runs that workflow. Null when there is none, or when the viewer cannot edit it
             */
            stage_path?: string | null;
        }> | null;
        /**
         * how many of the attached stages the in-use notice cannot list: one on another account in the tree, one on a private opening the viewer cannot see, one whose MultiStage parent is gone, one soft-deleted. The delete guard refuses over them, so a notice reading `usages` as the whole story would tell an admin to detach everything it names and still be refused. `usages.length` + this = the distinct stages attached, which can be less than usage_count because one stage may hold the template in two document sets. Null on an inherited row (AXHE-4978)
         */
        unlisted_usage_count?: number | null;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/workflow_editor/embedded_signature_templates/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `not found`,
                422: `invalid (e.g. a blank title), or blocked (turning off available_to_sub_accounts while a sub-account stage still references the template)`,
            },
        });
    }

    /**
     * Delete a legacy template (soft-delete); blocked while assigned to any stage
     * @param id
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiWorkflowEditorEmbeddedSignatureTemplates(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/workflow_editor/embedded_signature_templates/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `the user cannot manage legacy document templates`,
                404: `not found`,
                422: `blocked — template is assigned to one or more stages`,
            },
        });
    }

    /**
     * Grouped, typed catalog of mappable Fountain fields
     * @returns any returns grouped fields
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorOfferLetterFieldCatalog(): CancelablePromise<{
        groups?: Array<{
            /**
             * what the values in this group are ABOUT (AXHE-4180). Groups come back in this order and an empty one is omitted. "Hiring process" holds the portal-link and interview-slot vocabulary the other four do not describe
             */
            category?: 'Applicant' | 'Opening' | 'Offer' | 'Company' | 'Hiring process';
            fields?: Array<{
                key?: string;
                title?: string;
                data_type?: string;
                options?: Array<{
                    value?: string;
                    label?: string;
                }>;
            }>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/offer_letter_field_catalog',
        });
    }

    /**
     * Create a data field inline (becomes mappable immediately)
     * @param requestBody
     * @returns any data field created
     * @throws ApiError
     */
    public static postInternalApiWorkflowEditorDataFields(
        requestBody: {
            data_field?: {
                key: string;
                title?: string;
            };
        },
    ): CancelablePromise<{
        key?: string;
        title?: string;
        data_type?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/workflow_editor/data_fields',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `invalid data field`,
            },
        });
    }

    /**
     * List offer letter templates (server-side search, sort + pagination)
     * @param query title search (ILIKE)
     * @param page
     * @param perPage
     * @param sort column to sort by; defaults to title. An unrecognised value falls back to the default rather than erroring.
     * @param direction sort direction; anything other than "desc" is ascending
     * @returns any returns the account templates
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorOfferLetterTemplates(
        query?: string,
        page?: number,
        perPage?: number,
        sort?: 'title' | 'updated_at',
        direction?: 'asc' | 'desc',
    ): CancelablePromise<{
        offer_letter_templates?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            title?: string;
            description?: string | null;
            active?: boolean;
            available_to_sub_accounts?: boolean;
            /**
             * mappable variables detected in the document — excludes conditional blocks and signature placeholders (AXHE-4374)
             */
            variables_count?: number;
            /**
             * how many of variables_count have a Fountain field mapped (AXHE-4389)
             */
            mapped_variables_count?: number;
            /**
             * conditional BLOCKS in the document, not conditions — one {{#block}} may hold several
             */
            conditions_count?: number;
            /**
             * how many of conditions_count carry at least one condition, so they decide when the block renders (AXHE-4389)
             */
            configured_conditions_count?: number;
            updated_at?: string;
            /**
             * display name of whoever last uploaded/edited the template
             */
            updated_by?: string | null;
            /**
             * number of stages this template is assigned to; null on an inherited template, whose assignments belong to the owning account (AXHE-3879)
             */
            usage_count?: number | null;
            /**
             * how many of the attached stages the in-use notice cannot list: one on another account in the tree, one on a private opening the viewer cannot see, one whose MultiStage parent is gone, one soft-deleted. The delete guard refuses over them, so a notice reading `usages` as the whole story would tell an admin to detach everything it names and still be refused. `usages.length` + this = the distinct stages attached, which can be less than usage_count because one stage may hold the template in two document sets. Null on an inherited row (AXHE-4978)
             */
            unlisted_usage_count?: number | null;
            /**
             * number of DISTINCT workflows holding this template — the figure the in-use notice counts in its sentence. Not usage_count, which counts stage assignments: one workflow can hold two document-signing stages that use the same template. Null on an inherited template, like usage_count (AXHE-4978)
             */
            workflow_usage_count?: number | null;
            /**
             * one entry per stage assignment, ordered by workflow then stage — the rows the in-use notice lists. Null on an inherited template (AXHE-4978)
             */
            usages?: Array<{
                stage_title?: string;
                /**
                 * the workflow holding the stage: a Workflow record on an account running the workflow system, otherwise the opening's own stage set
                 */
                workflow_title?: string;
                /**
                 * Recruiter UI path that opens the stage in the workflow editor, through its own opening or, for a stage a Workflow record owns, the first opening on this account that runs that workflow. Null when there is none, or when the viewer cannot edit it
                 */
                stage_path?: string | null;
            }> | null;
            /**
             * true when the template belongs to an ancestor account and is read-only here (AXHE-3879)
             */
            inherited?: boolean;
            signers?: Array<{
                /**
                 * unique signer key (roster identity)
                 */
                'k'?: string;
                /**
                 * display label (signer's name)
                 */
                'l'?: string;
                /**
                 * single-letter avatar initial
                 */
                's'?: string;
                /**
                 * avatar foreground color (hex)
                 */
                fg?: string;
                /**
                 * avatar background color (hex)
                 */
                bg?: string;
                /**
                 * true on the one entry that is the candidate (applicant); omitted or false on employer countersigners. A non-empty roster must mark exactly one, else the update is rejected.
                 */
                'c'?: boolean;
            }>;
        }>;
        pagination?: {
            current_page?: number;
            next_page?: number | null;
            per_page?: number | null;
            total_count?: number;
            total_pages?: number;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/offer_letter_templates',
            query: {
                'query': query,
                'page': page,
                'per_page': perPage,
                'sort': sort,
                'direction': direction,
            },
        });
    }

    /**
     * Show an offer letter template with its detected fields
     * @param externalId
     * @returns any returns the template
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorOfferLetterTemplates1(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        title?: string;
        description?: string | null;
        /**
         * shown to the applicant in the send-offer flow
         */
        applicant_message?: string | null;
        active?: boolean;
        available_to_sub_accounts?: boolean;
        signers?: Array<{
            /**
             * unique signer key (roster identity)
             */
            'k'?: string;
            /**
             * display label (signer's name)
             */
            'l'?: string;
            /**
             * single-letter avatar initial
             */
            's'?: string;
            /**
             * avatar foreground color (hex)
             */
            fg?: string;
            /**
             * avatar background color (hex)
             */
            bg?: string;
            /**
             * true on the one entry that is the candidate (applicant); omitted or false on employer countersigners. A non-empty roster must mark exactly one, else the update is rejected.
             */
            'c'?: boolean;
        }>;
        /**
         * original .docx filename, for the top bar "Document" chip
         */
        document_file_name?: string | null;
        /**
         * true when the template belongs to an ancestor account and is read-only here (AXHE-3879)
         */
        inherited?: boolean;
        template_fields?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            key?: string;
            kind?: 'auto_readonly' | 'manual_entry' | 'free_text' | 'conditional_toggle' | 'signature';
            mapping_status?: 'mapped' | 'unmapped';
            mapped_to?: string | null;
            currency_format?: boolean;
            label?: string | null;
            data_type?: 'text' | 'number' | 'currency' | 'date' | 'address' | 'boolean' | 'select';
            spell_out?: boolean;
            formulas?: Array<string>;
            /**
             * select fields only (AXHE-4180): the authored choices the Send Offer form renders as a dropdown. Empty for every other data type
             */
            options?: Array<string>;
            conditions?: Array<{
                /**
                 * external_id (UUID)
                 */
                id?: string;
                data_key?: string;
                operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                value?: string | null;
                /**
                 * the in / not_in entries, already split — present for those two operators only. Read this rather than splitting `value`, which cannot express an entry containing a comma (AXHE-4763)
                 */
                values?: Array<string>;
            }>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/offer_letter_templates/{external_id}',
            path: {
                'external_id': externalId,
            },
            errors: {
                404: `not found`,
            },
        });
    }

    /**
     * Update template config (signer roster, inline name)
     * @param externalId
     * @param requestBody
     * @returns any updated
     * @throws ApiError
     */
    public static patchInternalApiWorkflowEditorOfferLetterTemplates(
        externalId: string,
        requestBody: {
            offer_letter_template?: {
                title?: string;
                description?: string;
                applicant_message?: string;
                available_to_sub_accounts?: boolean;
                signers?: Array<{
                    /**
                     * unique signer key (roster identity)
                     */
                    'k'?: string;
                    /**
                     * display label (signer's name)
                     */
                    'l'?: string;
                    /**
                     * single-letter avatar initial
                     */
                    's'?: string;
                    /**
                     * avatar foreground color (hex)
                     */
                    fg?: string;
                    /**
                     * avatar background color (hex)
                     */
                    bg?: string;
                    /**
                     * true on the one entry that is the candidate (applicant); omitted or false on employer countersigners. A non-empty roster must mark exactly one, else the update is rejected.
                     */
                    'c'?: boolean;
                }>;
            };
        },
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        title?: string;
        description?: string | null;
        /**
         * shown to the applicant in the send-offer flow
         */
        applicant_message?: string | null;
        active?: boolean;
        available_to_sub_accounts?: boolean;
        signers?: Array<{
            /**
             * unique signer key (roster identity)
             */
            'k'?: string;
            /**
             * display label (signer's name)
             */
            'l'?: string;
            /**
             * single-letter avatar initial
             */
            's'?: string;
            /**
             * avatar foreground color (hex)
             */
            fg?: string;
            /**
             * avatar background color (hex)
             */
            bg?: string;
            /**
             * true on the one entry that is the candidate (applicant); omitted or false on employer countersigners. A non-empty roster must mark exactly one, else the update is rejected.
             */
            'c'?: boolean;
        }>;
        /**
         * original .docx filename, for the top bar "Document" chip
         */
        document_file_name?: string | null;
        /**
         * true when the template belongs to an ancestor account and is read-only here (AXHE-3879)
         */
        inherited?: boolean;
        template_fields?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            key?: string;
            kind?: 'auto_readonly' | 'manual_entry' | 'free_text' | 'conditional_toggle' | 'signature';
            mapping_status?: 'mapped' | 'unmapped';
            mapped_to?: string | null;
            currency_format?: boolean;
            label?: string | null;
            data_type?: 'text' | 'number' | 'currency' | 'date' | 'address' | 'boolean' | 'select';
            spell_out?: boolean;
            formulas?: Array<string>;
            /**
             * select fields only (AXHE-4180): the authored choices the Send Offer form renders as a dropdown. Empty for every other data type
             */
            options?: Array<string>;
            conditions?: Array<{
                /**
                 * external_id (UUID)
                 */
                id?: string;
                data_key?: string;
                operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                value?: string | null;
                /**
                 * the in / not_in entries, already split — present for those two operators only. Read this rather than splitting `value`, which cannot express an entry containing a comma (AXHE-4763)
                 */
                values?: Array<string>;
            }>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/workflow_editor/offer_letter_templates/{external_id}',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Delete a template (soft-delete); blocked while assigned to any stage
     * @param externalId
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiWorkflowEditorOfferLetterTemplates(
        externalId: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/workflow_editor/offer_letter_templates/{external_id}',
            path: {
                'external_id': externalId,
            },
            errors: {
                404: `not found`,
                422: `blocked — template is assigned to one or more stages`,
            },
        });
    }

    /**
     * Rendered HTML body for the editor canvas (tokens wrapped for highlight/click)
     * @param externalId
     * @returns any rendered html body
     * @throws ApiError
     */
    public static getInternalApiWorkflowEditorOfferLetterTemplatesPreview(
        externalId: string,
    ): CancelablePromise<{
        html?: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/workflow_editor/offer_letter_templates/{external_id}/preview',
            path: {
                'external_id': externalId,
            },
        });
    }

    /**
     * Clone a readable template into an editable copy owned by the current account
     * Deep-copies the template (document bytes, fields, and each field's conditions)
     * into the current account. The source may be one this account owns or one
     * adopted from an ancestor account (available_to_sub_accounts) — an adopted
     * template is read-only, so cloning is how a sub-account gets a version it can
     * edit. The copy is independent: later edits on either side do not propagate.
     * Titled "Copy of <title>", numbered if that name is already taken. AXHE-3879.
     *
     * @param externalId
     * @returns any the new, independently-owned copy
     * @throws ApiError
     */
    public static postInternalApiWorkflowEditorOfferLetterTemplatesClone(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        title?: string;
        description?: string | null;
        /**
         * shown to the applicant in the send-offer flow
         */
        applicant_message?: string | null;
        active?: boolean;
        available_to_sub_accounts?: boolean;
        signers?: Array<{
            /**
             * unique signer key (roster identity)
             */
            'k'?: string;
            /**
             * display label (signer's name)
             */
            'l'?: string;
            /**
             * single-letter avatar initial
             */
            's'?: string;
            /**
             * avatar foreground color (hex)
             */
            fg?: string;
            /**
             * avatar background color (hex)
             */
            bg?: string;
            /**
             * true on the one entry that is the candidate (applicant); omitted or false on employer countersigners. A non-empty roster must mark exactly one, else the update is rejected.
             */
            'c'?: boolean;
        }>;
        /**
         * original .docx filename, for the top bar "Document" chip
         */
        document_file_name?: string | null;
        /**
         * true when the template belongs to an ancestor account and is read-only here (AXHE-3879)
         */
        inherited?: boolean;
        template_fields?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            key?: string;
            kind?: 'auto_readonly' | 'manual_entry' | 'free_text' | 'conditional_toggle' | 'signature';
            mapping_status?: 'mapped' | 'unmapped';
            mapped_to?: string | null;
            currency_format?: boolean;
            label?: string | null;
            data_type?: 'text' | 'number' | 'currency' | 'date' | 'address' | 'boolean' | 'select';
            spell_out?: boolean;
            formulas?: Array<string>;
            /**
             * select fields only (AXHE-4180): the authored choices the Send Offer form renders as a dropdown. Empty for every other data type
             */
            options?: Array<string>;
            conditions?: Array<{
                /**
                 * external_id (UUID)
                 */
                id?: string;
                data_key?: string;
                operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                value?: string | null;
                /**
                 * the in / not_in entries, already split — present for those two operators only. Read this rather than splitting `value`, which cannot express an entry containing a comma (AXHE-4763)
                 */
                values?: Array<string>;
            }>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/workflow_editor/offer_letter_templates/{external_id}/clone',
            path: {
                'external_id': externalId,
            },
            errors: {
                404: `not found — the source is not readable by this account`,
                422: `the copy could not be saved (validation failure or title collision)`,
            },
        });
    }

    /**
     * Resolve a detected field (map an unmapped key, set kind/currency)
     * @param offerLetterTemplateExternalId
     * @param externalId
     * @param requestBody
     * @returns any field updated
     * @throws ApiError
     */
    public static patchInternalApiWorkflowEditorOfferLetterTemplatesFields(
        offerLetterTemplateExternalId: string,
        externalId: string,
        requestBody: {
            field?: {
                kind?: string;
                mapping_status?: string;
                mapped_to?: string;
                currency_format?: boolean;
                label?: string;
                data_type?: string;
                spell_out?: boolean;
                formulas?: Array<string>;
                options?: Array<string>;
            };
        },
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        key?: string;
        kind?: 'auto_readonly' | 'manual_entry' | 'free_text' | 'conditional_toggle' | 'signature';
        mapping_status?: 'mapped' | 'unmapped';
        mapped_to?: string | null;
        currency_format?: boolean;
        label?: string | null;
        data_type?: 'text' | 'number' | 'currency' | 'date' | 'address' | 'boolean' | 'select';
        spell_out?: boolean;
        formulas?: Array<string>;
        /**
         * select fields only (AXHE-4180): the authored choices the Send Offer form renders as a dropdown. Empty for every other data type
         */
        options?: Array<string>;
        conditions?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            data_key?: string;
            operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
            value?: string | null;
            /**
             * the in / not_in entries, already split — present for those two operators only. Read this rather than splitting `value`, which cannot express an entry containing a comma (AXHE-4763)
             */
            values?: Array<string>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/workflow_editor/offer_letter_templates/{offer_letter_template_external_id}/fields/{external_id}',
            path: {
                'offer_letter_template_external_id': offerLetterTemplateExternalId,
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Replace the conditions behind a conditional block (OR-combined)
     * @param offerLetterTemplateExternalId
     * @param fieldExternalId
     * @param requestBody
     * @returns any conditions replaced; returns the updated field
     * @throws ApiError
     */
    public static patchInternalApiWorkflowEditorOfferLetterTemplatesFieldsConditions(
        offerLetterTemplateExternalId: string,
        fieldExternalId: string,
        requestBody: {
            conditions?: Array<{
                data_key: string;
                operator: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                value?: string | null;
                /**
                 * the in / not_in entries. Send this instead of a comma-joined `value`: only the array can carry an entry that contains a comma, and it wins when both are sent (AXHE-4763)
                 */
                values?: Array<string>;
            }>;
        },
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        key?: string;
        kind?: 'auto_readonly' | 'manual_entry' | 'free_text' | 'conditional_toggle' | 'signature';
        mapping_status?: 'mapped' | 'unmapped';
        mapped_to?: string | null;
        currency_format?: boolean;
        label?: string | null;
        data_type?: 'text' | 'number' | 'currency' | 'date' | 'address' | 'boolean' | 'select';
        spell_out?: boolean;
        formulas?: Array<string>;
        /**
         * select fields only (AXHE-4180): the authored choices the Send Offer form renders as a dropdown. Empty for every other data type
         */
        options?: Array<string>;
        conditions?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            data_key?: string;
            operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
            value?: string | null;
            /**
             * the in / not_in entries, already split — present for those two operators only. Read this rather than splitting `value`, which cannot express an entry containing a comma (AXHE-4763)
             */
            values?: Array<string>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/workflow_editor/offer_letter_templates/{offer_letter_template_external_id}/fields/{field_external_id}/conditions',
            path: {
                'offer_letter_template_external_id': offerLetterTemplateExternalId,
                'field_external_id': fieldExternalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `a condition is invalid, the field is not a conditional block, or the rules would leave a variable rendering while hidden`,
            },
        });
    }

}